import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "../../lib/utils"

/** Shared action primitive. Height grows with the label; touch sizing uses theme tokens. */
const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-1.5 min-w-0 max-w-full whitespace-normal break-words rounded-md text-[13px] font-medium tracking-[-0.005em]",
    "transition-[background-color,border-color,color,box-shadow,transform] duration-150",
    "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background",
    "disabled:pointer-events-none disabled:opacity-50",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0",
    "active:scale-[0.98]",
  ].join(' '),
  {
    variants: {
      variant: {
        default: [
          "bg-primary text-primary-foreground",
          "shadow-[0_1px_0_0_rgba(255,255,255,0.08)_inset,_0_1px_2px_0_rgba(0,0,0,0.1)]",
          "hover:bg-primary/90",
        ].join(' '),
        destructive: [
          "bg-destructive text-destructive-foreground",
          "shadow-[0_1px_0_0_rgba(255,255,255,0.1)_inset,_0_1px_2px_0_rgba(0,0,0,0.1)]",
          "hover:bg-destructive/90",
        ].join(' '),
        outline: [
          "border border-border bg-card text-foreground",
          "shadow-[0_1px_0_0_rgba(255,255,255,0.04)_inset]",
          "hover:bg-muted hover:border-muted-foreground/25",
        ].join(' '),
        secondary: [
          "bg-secondary text-secondary-foreground border border-border/50",
          "hover:bg-muted",
        ].join(' '),
        ghost: "text-foreground hover:bg-muted",
        link: "text-foreground underline-offset-4 hover:underline px-0",
      },
      size: {
        default: "min-h-9 h-auto px-3 py-2 leading-5 [&_svg]:size-4",
        sm: "min-h-8 h-auto px-2.5 py-1.5 text-[12px] leading-5 [&_svg]:size-3.5",
        lg: "min-h-10 h-auto px-4 py-2 text-[14px] leading-5 [&_svg]:size-4",
        icon: "h-9 w-9 shrink-0 p-0 [&_svg]:size-4",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      data-slot="button"
      data-size={size || "default"}
      data-variant={variant || "default"}
      {...props} />
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
